import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";


export const API = config.POSTLOGIN_API_URL_COMPANY;

const inFlightGets = new Map();

export const fetchNetzero = (url) => {
  if (inFlightGets.has(url)) return inFlightGets.get(url);
  const p = apiCall(url, {}, {}, "GET", true, false).finally(() => inFlightGets.delete(url));
  inFlightGets.set(url, p);
  return p;
};

export const TABS = [
  { key: "scope-1", label: "Scope 1", icon: "fire" },
  { key: "scope-2", label: "Scope 2", icon: "bolt" },
  { key: "scope-3", label: "Scope 3", icon: "globe" },
];

export const num = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

export const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

export const statusLabel = { ON_TRACK: "On Track", OFF_TRACK: "Off Track", NO_DATA: "—" };

export const mostRecentActual = (pathway, fallbackYear) => {
  const act = (pathway || []).filter((r) => r.actual != null);
  const last = act[act.length - 1];
  return last ? { year: last.year, value: last.actual } : { year: fallbackYear, value: null };
};

export const actualAtYear = (pathway, y) => pathway.find((r) => r.year === y)?.actual ?? null;

export const scopeSnapshot = (s1, s2, s3, targetYear) => {
  const mry1 = mostRecentActual(s1.rows, s1.baseYear);
  const mry2 = mostRecentActual(s2.rows, s1.baseYear);
  const mry3 = mostRecentActual(s3?.pathway, s1.baseYear);
  const mryYear = Math.max(mry1.year, mry2.year, mry3.year);
  const targetAt = (p) => (p || []).find((r) => r.year === targetYear)?.required ?? null;
  return {
    mryYear,
    act1: actualAtYear(s1.rows, mryYear) ?? mry1.value,
    act2: actualAtYear(s2.rows, mryYear) ?? mry2.value,
    act3: s3 ? (actualAtYear(s3.pathway, mryYear) ?? mry3.value) : null,
    target1: targetAt(s1.rows),
    target2: targetAt(s2.rows),
    target3: s3 ? targetAt(s3.pathway) : null,
  };
};

export function requiredFromNow(rows, netZeroYear) {
  const actuals = (rows || []).filter((r) => r.actual != null);
  if (!actuals.length || !netZeroYear) return null;
  const last = actuals[actuals.length - 1];
  const fromYear = Number(last.year);
  const fromValue = Number(last.actual);
  const floorRow = (rows || []).find((r) => Number(r.year) === Number(netZeroYear));
  const floor = floorRow ? Number(floorRow.required) : null;
  const years = Number(netZeroYear) - fromYear;
  if (floor == null || years <= 0 || fromValue <= 0 || fromValue <= floor) return null;
  const ratePct = (1 - Math.pow(Math.max(floor, 1) / fromValue, 1 / years)) * 100;
  return { fromYear, fromValue, floor, netZeroYear: Number(netZeroYear), ratePct };
}

export const writeSheet = (rows, { sheetName, fileName, numberCols = [], minColWidth = 14 }) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  rows.forEach((_, i) =>
    numberCols.forEach((c) => {
      const cell = ws[XLSX.utils.encode_cell({ c, r: i + 1 })];
      if (cell && cell.t === "n") cell.z = "#,##0.##";
    }),
  );
  ws["!cols"] = Object.keys(rows[0] || {}).map((h) => ({ wch: Math.max(h.length + 2, minColWidth) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`);
};

export const downloadExcel = (pathwayRows, label) => {
  const sheet = (pathwayRows || []).map((r) => ({
    "Year": r.year,
    "Required (tCO₂e)": r.required != null ? Number(Number(r.required).toFixed(2)) : "",
    "% Reduction from Base": r.pctReduction != null ? `${(Number(r.pctReduction) * 100).toFixed(1)}%` : "",
    "Annual Reduction (tCO₂e)": r.annualReduction != null ? Number(Number(r.annualReduction).toFixed(2)) : "",
    "Actual (tCO₂e)": r.actual != null ? Number(Number(r.actual).toFixed(2)) : "",
    "Status": r.status === "ON_TRACK" ? "On Track" : r.status === "OFF_TRACK" ? "Off Track" : "—",
    "Deviation": r.actual != null && r.required != null && r.required > 0
      ? `${r.status === "OFF_TRACK" ? "+" : "-"}${Math.abs(((r.actual - r.required) / r.required) * 100).toFixed(1)}%`
      : "",
  }));
  writeSheet(sheet, {
    sheetName: label,
    fileName: `${label.replace(/\s+/g, "_")}_Net_Zero_Pathway.xlsx`,
    numberCols: [1, 3, 4],
  });
};

export const downloadCategoryMatrix = (cats) => {
  const base = cats[0]?.pathway || [];
  const sheet = base.map((row) => {
    const out = { Year: row.year };
    for (const c of cats) {
      const r = c.pathway.find((x) => x.year === row.year);
      out[`${c.name} — Required`] = r?.required != null ? Number(Number(r.required).toFixed(2)) : "";
      out[`${c.name} — Actual`] = r?.actual != null ? Number(Number(r.actual).toFixed(2)) : "";
    }
    return out;
  });
  const numberCols = Object.keys(sheet[0] || {}).map((_, i) => i).slice(1);
  writeSheet(sheet, { sheetName: "Scope 3 by Category", fileName: "Scope_3_By_Category_Net_Zero.xlsx", numberCols });
};

export const CAT_COLORS = [
  "#3f88a5", "#c0392b", "#3f8a5c", "#b8860b", "#7a6ba8", "#2c7da0", "#a83279", "#5c8001",
  "#c76e00", "#4a5568", "#0f766e", "#9d174d", "#3730a3", "#7c2d12", "#166534",
];

export function useNetzeroResource(url) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await fetchNetzero(url);
      if (!mounted) return;
      if (res?.isSuccess && res.data) {
        setData(res.data);
        setError("");
      } else {
        setData(null);
        setError(res?.message || "Could not load the net-zero target.");
      }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [url]);
  return { data, loading, error };
}

/**
 * Fetches settings + all three scope pathways once, so the scope tabs and the
 * horizon dashboard share one load instead of each hitting the same endpoints.
 * @returns {{loading:boolean, settings:object|null, scopes:{1:object|null,2:object|null,3:object|null}}}
 */
export function useNetzeroData() {
  const [state, setState] = useState({ loading: true, settings: null, scopes: { 1: null, 2: null, 3: null } });
  useEffect(() => {
    let mounted = true;
    (async () => {
      const [settingsRes, s1, s2, s3] = await Promise.all([
        fetchNetzero(`${API}netzero/settings`),
        fetchNetzero(`${API}netzero/scope1`),
        fetchNetzero(`${API}netzero/scope2`),
        fetchNetzero(`${API}netzero/scope3`),
      ]);
      if (!mounted) return;
      const ok = (res) => (res?.isSuccess && res.data ? res.data : null);
      setState({
        loading: false,
        settings: settingsRes?.isSuccess ? (settingsRes.data?.setting || null) : null,
        scopes: { 1: ok(s1), 2: ok(s2), 3: ok(s3) },
      });
    })();
    return () => { mounted = false; };
  }, []);
  return state;
}

export const dashboardStatusLabel = { ACHIEVED: "Achieved", ON_TRACK: "On Track", OFF_TRACK: "Off Track", NO_DATA: "—" };

export const getDashboardStatus = (base, target, actual, pathwayRows, year, higherIsBetter = false) => {
  if (actual == null) return "NO_DATA";
  if (higherIsBetter) {
    if (actual >= target) return "ACHIEVED";
    return "OFF_TRACK";
  }
  if (actual <= target) return "ACHIEVED";
  const row = pathwayRows?.find(r => r.year === year);
  if (row) {
    return row.status === "ON_TRACK" ? "ON_TRACK" : "OFF_TRACK";
  }
  return "OFF_TRACK";
};

export const formatDashboardValue = (val, type) => {
  if (val == null || val === "—") return "—";
  if (type === "percentage") {
    return `${(Number(val) * 100).toFixed(1)}%`;
  }
  if (type === "currency") {
    return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export const calcDashboardCompletion = (row) => {
  const base = row.baseValue;
  const target = row.targetValue;
  const actual = row.actualValue !== undefined ? row.actualValue : row.trajectory;
  if (base == null || target == null || actual == null) return "—";
  const denominator = row.higherIsBetter ? (target - base) : (base - target);
  if (denominator === 0) {
    const met = row.higherIsBetter ? actual >= target : actual <= target;
    return met ? "100.0%" : "0.0%";
  }
  const completion = row.higherIsBetter 
    ? ((actual - base) / denominator) * 100 
    : ((base - actual) / denominator) * 100;
  return `${completion.toFixed(1)}%`;
};

export const prepareNearTermRows = (s1, s2, s3) => {
  if (!s1 || !s2) return [];
  const ntYear = s1.nearTermTargetYear;
  const { mryYear, act1, act2, act3, target1, target2, target3 } = scopeSnapshot(s1, s2, s3, ntYear);

  const row = (kpi, baseYear, base, target, act, pathway, minAmbition) => ({
    kpi, baseYear, baseValue: base, targetYear: ntYear, targetValue: target,
    actualYear: mryYear, actualValue: act, minAmbition,
    status: getDashboardStatus(base, target, act, pathway, mryYear), type: "emission",
  });

  const rows = [
    row("Scope 1 (tCO₂e)", s1.baseYear, s1.baseEmissions, target1, act1, s1.rows, "1.5°C"),
    row("Scope 2 (tCO₂e)", s2.baseYear, s2.baseEmissions, target2, act2, s2.rows, "1.5°C"),
  ];

  if (s3) {
    s3.categories.forEach((c) => {
      if (c.baseEmissions > 0) {
        const cAct = actualAtYear(c.pathway, mryYear) ?? mostRecentActual(c.pathway, s1.baseYear).value;
        const cTarget = c.pathway.find((r) => r.year === ntYear)?.required ?? null;
        rows.push(row(`Scope 3 ${c.categoryKey.replace("CAT_", "Cat ")} — ${c.name} (tCO₂e)`, s3.baseYear, c.baseEmissions, cTarget, cAct, c.pathway, "Well-below 2°C"));
      }
    });
    rows.push(row("Scope 3 Total (tCO₂e)", s3.baseYear, s3.totalBaseEmissions, target3, act3, s3.pathway, "Well-below 2°C"));
  }
  return rows;
};

export const prepareLongTermRows = (s1, s2, s3) => {
  if (!s1 || !s2) return [];
  const ltYear = s1.longTermTargetYear;
  const { mryYear, act1, act2, act3, target1, target2, target3 } = scopeSnapshot(s1, s2, s3, ltYear);

  const row = (kpi, baseYear, base, target, act, pathway) => ({
    kpi, baseYear, baseValue: base, targetYear: ltYear, targetValue: target,
    minReduction: "90%", trajectory: act, actualYear: mryYear, residual: "Absolute contraction",
    status: getDashboardStatus(base, target, act, pathway, mryYear), type: "emission",
  });

  const rows = [
    row("Scope 1 (tCO₂e)", s1.baseYear, s1.baseEmissions, target1, act1, s1.rows),
    row("Scope 2 (tCO₂e)", s2.baseYear, s2.baseEmissions, target2, act2, s2.rows),
  ];
  if (s3) {
    rows.push(row("Scope 3 Total (tCO₂e)", s3.baseYear, s3.totalBaseEmissions, target3, act3, s3.pathway));
  }
  return rows;
};

export const prepareAnnualRows = (s1, s2, s3, selectedYear) => {
  if (!s1 || !s2 || !selectedYear) return [];

  const getValAtYear = (pathway, y, field) => pathway.find(r => r.year === y)?.[field] ?? null;
  const getStatusAtYear = (pathway, y) => {
    const s = pathway.find(r => r.year === y)?.status;
    return s === "ON_TRACK" ? "ON_TRACK" : s === "OFF_TRACK" ? "OFF_TRACK" : "NO_DATA";
  };

  const target1 = getValAtYear(s1.rows, selectedYear, "required");
  const act1 = getValAtYear(s1.rows, selectedYear, "actual");
  const status1 = getStatusAtYear(s1.rows, selectedYear);

  const target2 = getValAtYear(s2.rows, selectedYear, "required");
  const act2 = getValAtYear(s2.rows, selectedYear, "actual");
  const status2 = getStatusAtYear(s2.rows, selectedYear);

  const rows = [
    {
      kpi: "Scope 1 (tCO₂e)",
      baseValue: s1.baseEmissions,
      targetValue: target1,
      actualValue: act1,
      status: status1 === "ON_TRACK" && act1 <= target1 ? "ACHIEVED" : status1,
      type: "emission"
    },
    {
      kpi: "Scope 2 (tCO₂e)",
      baseValue: s2.baseEmissions,
      targetValue: target2,
      actualValue: act2,
      status: status2 === "ON_TRACK" && act2 <= target2 ? "ACHIEVED" : status2,
      type: "emission"
    }
  ];

  if (s3) {
    s3.categories.forEach(c => {
      if (c.baseEmissions > 0) {
        const cTarget = getValAtYear(c.pathway, selectedYear, "required");
        const cAct = getValAtYear(c.pathway, selectedYear, "actual");
        const cStatus = getStatusAtYear(c.pathway, selectedYear);
        rows.push({
          kpi: `Scope 3 ${c.categoryKey.replace("CAT_", "Cat ")} — ${c.name} (tCO₂e)`,
          baseValue: c.baseEmissions,
          targetValue: cTarget,
          actualValue: cAct,
          status: cStatus === "ON_TRACK" && cAct <= cTarget ? "ACHIEVED" : cStatus,
          type: "emission"
        });
      }
    });

    const target3 = getValAtYear(s3.pathway, selectedYear, "required");
    const act3 = getValAtYear(s3.pathway, selectedYear, "actual");
    const status3 = getStatusAtYear(s3.pathway, selectedYear);
    rows.push({
      kpi: "Total Scope 3 (tCO₂e)",
      baseValue: s3.totalBaseEmissions,
      targetValue: target3,
      actualValue: act3,
      status: status3 === "ON_TRACK" && act3 <= target3 ? "ACHIEVED" : status3,
      type: "emission"
    });
  }

  return rows;
};

export const downloadDashboardExcel = (rows, label) => {
  const sheet = rows.map((r) => {
    const item = { KPI: r.kpi ?? r.kpi };
    if (r.baseYear != null) item["Base Year"] = r.baseYear;
    item["Base Value"] = r.baseValue != null ? formatDashboardValue(r.baseValue, r.type) : "—";
    if (r.targetYear != null) item["Target Year"] = r.targetYear;
    if (r.targetValue != null) item["Target Value"] = formatDashboardValue(r.targetValue, r.type);
    if (r.actualYear != null) item["Actual Year"] = r.actualYear;
    if (r.actualValue !== undefined) item["Actual Value"] = r.actualValue != null ? formatDashboardValue(r.actualValue, r.type) : "—";
    
    if (r.trajectory !== undefined) item["Current Trajectory"] = r.trajectory != null ? formatDashboardValue(r.trajectory, r.type) : "—";
    if (r.minReduction != null) item["Min Reduction"] = r.minReduction;
    if (r.residual != null) item["Residual"] = r.residual;

    item["% Target Completion / Toward Target"] = calcDashboardCompletion(r);
    item["Status"] = r.status === "ACHIEVED" ? "Achieved" : r.status === "ON_TRACK" ? "On Track" : r.status === "OFF_TRACK" ? "Off Track" : "—";
    return item;
  });
  writeSheet(sheet, { sheetName: label, fileName: `${label.replace(/\s+/g, "_")}.xlsx`, minColWidth: 16 });
};
